import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { useAuth_ResetPasswordMutation } from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '5vh',
    paddingLeft: '15vw',
    paddingRight: '15vw',
  },
  inputContainer: {
    marginTop: '2vh',
    marginBottom: '15px',
  },
  input: {
    display: 'flex',
    margin: 'auto',
  },
  inputError: {
    backgroundColor: '#FFF1F1',
  },
  actionContainer: {
    textAlign: 'center',
    marginTop: '50px',
    marginBottom: '50px',
  },
  renavigate: { marginTop: '25px', textDecoration: 'underline', cursor: 'pointer' },
  resetLink: {
    color: '#44D2F2',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginBottom: '63px',
  },
}));

const schema = yup.object().shape({
  password: yup.string().required('Required').min(6, 'Must be atleast 6 characters'),
  confirmPassword: yup
    .string()
    .nullable()
    .oneOf([yup.ref('password')], "Passwords don't match")
    .required('Required')
    .min(6, 'Must be atleast 6 characters'),
});

type ResetPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<Record<string, string>>();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<ResetPasswordFormValues>({ resolver: yupResolver(schema) });

  const [resetPassword, { loading: resetPasswordExecuting }] = useAuth_ResetPasswordMutation({
    onCompleted: () => history.push('/'),
    onError: (e) =>
      showAlert({
        message: 'Error resetting password: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const onSubmit = async (formData: ResetPasswordFormValues) => {
    await resetPassword({ variables: { id: id, password: formData.password } });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root}>
      <div className={classes.inputContainer}>
        <Controller
          name="password"
          control={control}
          render={({ field: { ref, name, onBlur, onChange, value } }) => (
            <FormTextField
              name={name}
              defaultValue={value}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              type="password"
              className={clsx(classes.input, errors.password && classes.inputError)}
              placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
              errorMessage={errors.password?.message}
              label="New Password"
            />
          )}
        />
      </div>
      <div className={classes.inputContainer}>
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field: { ref, name, onBlur, onChange, value } }) => (
            <FormTextField
              name={name}
              defaultValue={value}
              onChange={onChange}
              onBlur={onBlur}
              ref={ref}
              type="password"
              className={clsx(classes.input, errors.confirmPassword && classes.inputError)}
              placeholder="&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;"
              errorMessage={errors.confirmPassword?.message}
              label="Confirm New Password"
            />
          )}
        />
      </div>
      <div className={classes.actionContainer}>
        <Button
          type="submit"
          variant="contained"
          color="secondary"
          disabled={isSubmitting || resetPasswordExecuting}
        >
          Change My Password
        </Button>
        <Typography className={classes.renavigate}>
          <Link onClick={() => history.push('/')}>Go back to the sign in page</Link>
        </Typography>
      </div>
    </form>
  );
};

export default ResetPasswordForm;
